import React from 'react';
import { Stack } from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { medicationApi } from 'src/api/medication';
import { visitScopedPrescribingApi } from 'src/api/visit-scoped-prescribing';
import { translateKey } from 'src/locales/translate';
import { buildStatusChangePayload } from 'src/sections/@dashboard/profile/components/medication-form/medicationFormPayload';
import {
  facilityMedicationBody,
  useMedicationEditSeries,
} from 'src/sections/@dashboard/visit/hooks/useMedicationWriteSeries';
import { patientOrVisitRoute } from 'src/sections/@dashboard/warehouse/hooks/useClinicalWriteSeries';
import { useStockWriteError } from 'src/sections/@dashboard/warehouse/hooks/useStockWriteError';
import { useStockWriteFeedback } from 'src/sections/@dashboard/warehouse/hooks/useStockWriteFeedback';
import { forbiddenMessage } from 'src/utils/forbiddenMessage';
import MedicationFormModal, {
  MedicationsFormModalValuesProps,
} from 'src/sections/@dashboard/profile/components/medication-form/MedicationFormModal';
import MedicationSummaryTable from 'src/sections/@dashboard/profile/components/MedicationSummaryTable';
import {
  formatDosing,
  formatDuration,
} from 'src/sections/@dashboard/profile/components/medication-form/medicationDisplay';
import { GetProfileMedicationsDto } from 'woofwoof-api';
import VisitModalEmptyState from './VisitModalEmptyState';

interface VisitModalMedicationsTabProps {
  medications: GetProfileMedicationsDto[];
  canEdit: boolean;
  /**
   * The status moves even once the visit is locked: a course may outlast it,
   * and the visit's own course is not written to (#707).
   */
  canChangeStatus: boolean;
  visitId: string;
  patientId: string;
  organizationId?: string;
  onActivityChange: () => void;
}

export default function VisitModalMedicationsTab({
  medications,
  canEdit,
  canChangeStatus,
  visitId,
  patientId,
  onActivityChange,
}: VisitModalMedicationsTabProps): React.ReactElement {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const onStockWrite = useStockWriteFeedback();
  const writeError = useStockWriteError();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingMedicationId, setEditingMedicationId] = useState<string | null>(
    null,
  );

  const editingMedication = editingMedicationId
    ? medications.find((m) => m.id === editingMedicationId)
    : undefined;

  const isGuestVisit = !patientId;

  // Adding medications now goes through the global "Dodaj do wizyty" menu; this
  // tab only edits / deletes existing ones (modal opens via row click). R3-4:
  // every edit is a write series; a guest visit edits through the visit.
  const medicationSeries = useMedicationEditSeries({
    route: patientOrVisitRoute(patientId, visitId),
    editingId: editingMedicationId ?? '',
    onSuccess: () => handleSaved(),
    // A Warehouse refusal, else the backend's reason for a 403 (#707).
    onError: (error: unknown) =>
      writeError(
        forbiddenMessage(error) ||
          translateKey('snackbar.medicationUpdateError'),
      )(error),
  });

  const { mutate: deleteMedication, isLoading: isDeleting } = useMutation({
    mutationFn: (medicationId: string) =>
      isGuestVisit
        ? visitScopedPrescribingApi.deleteMedication(visitId, medicationId)
        : medicationApi.medicationControllerDeleteProfileMedicationInOrganization({
            profileId: patientId,
            medicationId,
            visitId,
          }),
    // A delete reverses the medication's stock.
    onSuccess: (response) => {
      onStockWrite(response);
      onActivityChange();
      enqueueSnackbar(t('visit.visitCourseRemoveSuccess'), {
        variant: 'success',
      });
      setIsModalOpen(false);
      setEditingMedicationId(null);
    },
    // M-1: a refused delete says why (e.g. the drug came from a treatment).
    onError: writeError(translateKey('snackbar.medicationDeleteError')),
  });

  const { mutateAsync: changeStatus, isLoading: isChangingStatus } =
    useMutation({
      mutationFn: (data: MedicationsFormModalValuesProps) =>
        medicationApi.medicationControllerChangeVisitMedicationStatus({
          visitId,
          medicationId: editingMedicationId || '',
          changeMedicationStatusDto: buildStatusChangePayload(data),
        }),
      onError: (error: unknown) =>
        enqueueSnackbar(
          forbiddenMessage(error) || t('visit.visitCourseAddError'),
          { variant: 'error' },
        ),
    });

  function handleSaved(): void {
    onActivityChange();
    enqueueSnackbar(t('snackbar.medicationUpdated'), { variant: 'success' });
    setIsModalOpen(false);
    setEditingMedicationId(null);
  }

  // A status move goes through its own endpoint, the only one still open once
  // the visit is locked; the rest of the form is an ordinary edit.
  const handleSubmit = async (
    data: MedicationsFormModalValuesProps,
    { recordChanged }: { recordChanged: boolean },
  ): Promise<void> => {
    // R3-4: an unconfirmed save only resends its frozen request.
    if (!medicationSeries.pending) {
      if (data.status !== editingMedication?.status) {
        await changeStatus(data);
      }
      if (!recordChanged || !canEdit) {
        handleSaved();
        return;
      }
    }
    medicationSeries.submit(
      editingMedicationId ?? '',
      facilityMedicationBody(data),
      data,
    );
  };

  const handleOpenEdit = (id: string): void => {
    setEditingMedicationId(id);
    setIsModalOpen(true);
  };

  const handleClose = (): void => {
    setIsModalOpen(false);
    setEditingMedicationId(null);
  };

  if (medications.length === 0 && !canEdit) {
    return (
      <VisitModalEmptyState message="Brak lekarstw przypisanych podczas tej wizyty" />
    );
  }

  const rows = medications.map((med) => ({
    id: med.id,
    name: med.name,
    dosing: formatDosing(med),
    duration: formatDuration(med),
    notes: med.notes,
  }));

  return (
    <>
      <Stack sx={{ gap: 1, flex: 1, minHeight: 0 }}>
        <MedicationSummaryTable
          rows={rows}
          canEdit={canEdit || canChangeStatus}
          onRowClick={(_, row) => handleOpenEdit(row.id!)}
          emptyMessage="Brak lekarstw przypisanych podczas tej wizyty"
        />
      </Stack>

      <MedicationFormModal
        isModalOpen={isModalOpen}
        closeModal={handleClose}
        medication={editingMedication}
        linkedReminder={editingMedication?.linkedReminder}
        onSubmit={handleSubmit}
        statusOnly={!canEdit}
        onDelete={
          editingMedicationId
            ? () => deleteMedication(editingMedicationId)
            : undefined
        }
        isCreating={false}
        isUpdating={medicationSeries.isLoading || isChangingStatus}
        isDeleting={isDeleting}
        singleReminderOnly={isGuestVisit}
        stockEnabled
        unconfirmedSave={medicationSeries.pending}
        unconfirmedBlocked={medicationSeries.blocked}
        unconfirmedValues={medicationSeries.frozenValues}
        stockWriteError={medicationSeries.lastError}
        ownerProfileId={patientId || undefined}
      />
    </>
  );
}
